import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { ChevronDown, ChevronRight } from 'lucide-react'
import clsx from 'clsx'
import type { MenuProps } from '../../core/navigation'
import MenuUserInfo from './MenuUserInfo'

export default function SidebarMenu({ navigation, user, onLogout }: MenuProps) {
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['Anagrafiche'])

  const toggleMenu = (name: string) => {
    setExpandedMenus((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    )
  }

  return (
    <aside className="fixed inset-y-0 left-0 w-64 bg-gray-900 text-white flex flex-col">
      {/* Logo */}
      <div className="h-16 flex-shrink-0 flex items-center px-6 border-b border-gray-800">
        <img src="/logo.png" alt="SmartTenant" className="w-10 h-10 rounded-lg" />
        <div className="ml-3">
          <h1 className="font-semibold text-white">SmartTenant</h1>
          <p className="text-xs text-primary-400">{(user as any)?.tenant_name || 'Tenant'}</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-1">
        {navigation.map((item) =>
          item.children ? (
            <div key={item.name}>
              <button
                onClick={() => toggleMenu(item.name)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
              >
                <div className="flex items-center gap-3">
                  <item.icon className="w-5 h-5" />
                  {item.name}
                </div>
                {expandedMenus.includes(item.name) ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {expandedMenus.includes(item.name) && (
                <div className="ml-4 mt-1 space-y-1">
                  {item.children.map((child) => (
                    <NavLink
                      key={child.name}
                      to={child.href}
                      className={({ isActive }) =>
                        clsx(
                          'flex items-center gap-3 px-4 py-2 rounded-lg text-sm transition-colors',
                          isActive
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                        )
                      }
                    >
                      <child.icon className="w-4 h-4" />
                      {child.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <NavLink
              key={item.name}
              to={item.href!}
              end={item.href === '/app'}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                )
              }
            >
              <item.icon className="w-5 h-5" />
              <span className="flex-1">{item.name}</span>
              {item.badge && item.badge > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                  {item.badge}
                </span>
              )}
            </NavLink>
          )
        )}
      </nav>

      {/* User */}
      <div className="flex-shrink-0 p-4 border-t border-gray-800">
        <MenuUserInfo user={user} onLogout={onLogout} />
      </div>
    </aside>
  )
}
